/**
 * Display formatting helpers.
 *
 * Time handling note: the architecture plan stores UTC and renders in the tenant's
 * timezone, but the agreed tenant contract only carries { id, name, role } and no
 * timezone. Until a tenant timezone is available, timestamps are rendered in the
 * viewer's local timezone and the zone is always shown, so an operator never has to
 * guess which clock a value belongs to.
 */

const DATE_TIME_FORMAT = new Intl.DateTimeFormat('zh-CN', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  timeZoneName: 'short',
});

const DATE_FORMAT = new Intl.DateTimeFormat('zh-CN', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value === '' ? '—' : value;
  return DATE_TIME_FORMAT.format(date);
}

export function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value === '' ? '—' : value;
  return DATE_FORMAT.format(date);
}

/**
 * Compact relative age for dense tables ("3 分钟前"). Falls back to an absolute
 * date beyond a week, where relative wording stops being useful.
 */
export function formatRelative(value: string, now: number = Date.now()): string {
  const date = new Date(value);
  const time = date.getTime();
  if (Number.isNaN(time)) return value === '' ? '—' : value;

  const seconds = Math.round((now - time) / 1000);
  if (seconds < 0) return formatDateTime(value);
  if (seconds < 60) return '刚刚';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${String(minutes)} 分钟前`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${String(hours)} 小时前`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${String(days)} 天前`;

  return formatDate(value);
}

/** Truncated identifier for narrow columns; full value stays in the title attr. */
export function shortId(id: string): string {
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}…`;
}

export function initials(value: string): string {
  const trimmed = value.trim();
  if (trimmed === '') return '?';
  const first = trimmed[0];
  return first === undefined ? '?' : first.toUpperCase();
}

/** Collapses newlines and repeated spaces so table cells stay one line. */
export function condense(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

const NUMBER_FORMAT = new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 2 });

/** Grouped figure for counts and quota units ("12,345"); non-finite values read "—". */
export function formatNumber(value: number): string {
  return Number.isFinite(value) ? NUMBER_FORMAT.format(value) : '—';
}

/** Ledger-style figure with an explicit sign for credits ("+1,200" / "-300" / "0"). */
export function formatSignedNumber(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return value > 0 ? `+${NUMBER_FORMAT.format(value)}` : NUMBER_FORMAT.format(value);
}

const pad2 = (value: number): string => String(value).padStart(2, '0');

/** Local wall-clock value for `<input type="datetime-local">` ("2026-10-21T09:30"). */
export function toDateTimeInputValue(date: Date): string {
  if (Number.isNaN(date.getTime())) return '';
  return `${String(date.getFullYear())}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(
    date.getHours(),
  )}:${pad2(date.getMinutes())}`;
}

/** Parses a `datetime-local` value as local time; `null` for an empty or invalid value. */
export function parseDateTimeInputValue(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value.trim());
  if (match === null) return null;
  const part = (index: number): number => Number(match[index] ?? '0');
  const month = part(2) - 1;
  const day = part(3);
  const date = new Date(part(1), month, day, part(4), part(5), part(6));
  // Reject rollovers such as 2026-02-31 instead of silently moving the date.
  if (date.getMonth() !== month || date.getDate() !== day || part(4) > 23 || part(5) > 59)
    return null;
  return date;
}
